import { useSidebarState } from '@src/hooks/useSidebarState';
import BrandKitIcon from '@src/svgs/BrandKitIcon';
import GearIcon from '@src/svgs/GearIcon';
import { Studio } from '@src/types.ts/general';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { SidebarTooltip } from '../SidebarTooltip';
import {
  LABEL_VARIANTS,
  SECTION_LABEL_VARIANTS,
  SIDEBAR_SPRING,
} from './sidebarMotion';

interface SidebarSetupSectionProps {
  currentStudio: Studio;
  collapsed: boolean;
  allowBrandKit: boolean;
  allowSettings: boolean;
  search: string;
}

const SidebarSetupSection: React.FC<SidebarSetupSectionProps> = ({
  currentStudio,
  collapsed,
  allowBrandKit,
  allowSettings,
  search,
}) => {
  const { showLabels } = useSidebarState();

  // Nothing to set up (e.g. translation reviewers): drop the whole section so
  // its "Setup" label doesn't hang over an empty list.
  if (!allowBrandKit && !allowSettings) return null;

  return (
    <Wrapper $collapsed={collapsed}>
      <SectionLabelSlot>
        <AnimatePresence initial={false}>
          {showLabels && (
            <motion.div
              key="setup-label"
              variants={SECTION_LABEL_VARIANTS}
              initial="hidden"
              animate="shown"
              exit="hidden"
              transition={SIDEBAR_SPRING}
            >
              <SectionLabel>Setup</SectionLabel>
            </motion.div>
          )}
        </AnimatePresence>
      </SectionLabelSlot>
      {allowBrandKit && (
        <SetupItem
          to={`/${Studio.brandKit}${search}`}
          $isSelected={currentStudio === Studio.brandKit}
          $collapsed={collapsed}
          data-testid="nav-item-brandKit"
        >
          <NavIcon>
            <BrandKitIcon />
          </NavIcon>
          <AnimatePresence initial={false}>
            {showLabels && (
              <motion.span
                key="brand-kit-label"
                variants={LABEL_VARIANTS}
                initial="hidden"
                animate="shown"
                exit="hidden"
                transition={SIDEBAR_SPRING}
                style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                Brand Kit
              </motion.span>
            )}
          </AnimatePresence>
          {!showLabels && <Tooltip>Brand Kit</Tooltip>}
        </SetupItem>
      )}
      {allowSettings && (
        <SetupItem
          to={`/${Studio.settings}${search}`}
          $isSelected={currentStudio === Studio.settings}
          $collapsed={collapsed}
          data-testid="nav-item-settings"
        >
          <NavIcon>
            {/* GearIcon defaults to 12x12; nav icons render at 20x20. */}
            <GearIcon width="20" height="20" />
          </NavIcon>
          <AnimatePresence initial={false}>
            {showLabels && (
              <motion.span
                key="settings-label"
                variants={LABEL_VARIANTS}
                initial="hidden"
                animate="shown"
                exit="hidden"
                transition={SIDEBAR_SPRING}
                style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                Settings
              </motion.span>
            )}
          </AnimatePresence>
          {!showLabels && <Tooltip>Settings</Tooltip>}
        </SetupItem>
      )}
    </Wrapper>
  );
};

export default SidebarSetupSection;

const Wrapper = styled.div<{ $collapsed: boolean }>`
  padding: 4px 10px 8px;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionLabelSlot = styled.div`
  height: 28px;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: flex-end;
  height: 28px;
  box-sizing: border-box;
  color: var(--text-muted);
  font-size: 11px;
  line-height: 14px;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.2px;
  padding: 0 8px 6px 12px;
  white-space: nowrap;
  overflow: hidden;
`;

const Tooltip = styled(SidebarTooltip)``;

const SetupItem = styled(NavLink)<{
  $isSelected: boolean;
  $collapsed: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 30px;
  box-sizing: border-box;
  gap: 10px;
  /* Constant padding so the icon's X stays put when sidebar width animates. */
  padding: 0 10px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;
  cursor: pointer;
  border: none !important;

  &:hover {
    background: var(--sidebar-hover);
    color: var(--text-secondary);
  }

  &:hover ${Tooltip} {
    opacity: 1;
  }

  ${p =>
    p.$isSelected &&
    css`
      background: var(--sidebar-active);
      color: var(--foreground);

      & > span:first-child {
        color: var(--accent);
        opacity: 1;
      }
    `}
`;

const NavIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  opacity: 0.85;
  color: inherit;

  svg {
    width: 100%;
    height: 100%;
  }
`;
